import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21 } from "styled-components";
import React from "react";
import type { DownloadRow } from "~/features/downloads/adapter";
import { useDownloads } from "~/features/downloads/context";
import { DownloadCellList } from "~/features/downloads/table/cells";
import { TableCell } from "~/features/downloads/table/table-cell";
import { TableTitle } from "~/features/downloads/table/table-title";
import { extractHeadings, shouldRenderColumn } from "~/features/downloads/visibility";
import { hasItems } from "~/features/app/utils";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { Select } from "~/features/ui/form/select";
import { Wrap } from "~/features/ui/wrap";
const PREFERRED_COLUMN_ORDER = ["OSDome", "OS0", "OS1", "OS1 Max"];
function sortHeadings(headings: string[]): string[] {
  return [...headings].sort((a, b) => {
    const idxA = PREFERRED_COLUMN_ORDER.indexOf(a);
    const idxB = PREFERRED_COLUMN_ORDER.indexOf(b);
    if (idxA === -1 && idxB === -1) return 0;
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;
    return idxA - idxB;
  });
}
function TableRow(props: {
  row: DownloadRow;
  headings: string[];
  activeHeading?: string;
}) {
  const {
    row,
    headings,
    activeHeading
  } = props;
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  if (hasItems(row.fullWidthCells)) {
    return <_StyledDiv $_css={[{
      "display": "flex",
      "flexDirection": "column",
      "@media (min-width: 1024px)": {
        "flexDirection": "row"
      }
    }, themeStyle.border]}>
        <_StyledTableCell>
          <_StyledDiv2>{row.heading}</_StyledDiv2>
        </_StyledTableCell>
        <_StyledTableCell2>
          <DownloadCellList cells={row.fullWidthCells} />
        </_StyledTableCell2>
      </_StyledDiv>;
  }
  function renderColumn(colHeading: string) {
    const cell = row.groupedCells.find(groupedCell => groupedCell.heading === colHeading);
    if (!cell) return null;
    return <DownloadCellList cells={cell.content} />;
  }
  return <_StyledDiv3 $_css2={[{
    "display": "flex",
    "flexDirection": "column",
    "@media (min-width: 1024px)": {
      "flexDirection": "row"
    }
  }, themeStyle.border]}>
      <_StyledTableCell3>
        <_StyledDiv4>{row.heading}</_StyledDiv4>
      </_StyledTableCell3>
      {headings.filter(heading => activeHeading ? heading === activeHeading : true).map((colHeading, idx) => <_StyledTableCell4 key={idx}>
            {renderColumn(colHeading)}
          </_StyledTableCell4>)}
    </_StyledDiv3>;
}

/**
 * Renders the Hardware section and the Hardware Accessories section together
 * so the product columns (OSDome, OS0, OS1, ...) stay aligned. The Hardware
 * rows and any columned accessory rows (Accessory CAD Models) share a single
 * horizontal-scroll container. Full-width accessory rows (Y-cable PDFs) render
 * below the scroll container at full page width so they don't add scroll
 * distance to the columned section above.
 */
export function HardwareAndAccessoriesTable(props: {
  hardwareRows: DownloadRow[];
  accessoryRows: DownloadRow[];
}) {
  const {
    hardwareRows,
    accessoryRows
  } = props;
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const {
    activeFirmwareVersion,
    activeHardwareVersion
  } = useDownloads();
  const headings = React.useMemo(() => {
    const combined = Array.from(new Set([...extractHeadings(hardwareRows), ...extractHeadings(accessoryRows)]));
    const filtered = combined.filter(heading => shouldRenderColumn({
      heading,
      rows: [...hardwareRows, ...accessoryRows],
      activeHardware: activeHardwareVersion,
      activeFirmware: activeFirmwareVersion
    }));
    return sortHeadings(filtered);
  }, [hardwareRows, accessoryRows, activeHardwareVersion, activeFirmwareVersion]);
  const [activeHeading, setActiveHeading] = React.useState(headings[0]);
  React.useEffect(() => {
    setActiveHeading(headings[0]);
  }, [headings]);
  const accessoryColumnRows = accessoryRows.filter(row => hasItems(row.groupedCells));
  const accessoryFullWidthRows = accessoryRows.filter(row => !hasItems(row.groupedCells) && hasItems(row.fullWidthCells));
  const hasHardware = hasItems(hardwareRows);
  const hasAccessoryColumns = hasItems(accessoryColumnRows);
  const hasAccessoryFullWidth = hasItems(accessoryFullWidthRows);
  if (!hasHardware && !hasAccessoryColumns && !hasAccessoryFullWidth) return null;
  return <_StyledSection $_css3={[{
    "position": "relative",
    "zIndex": "10",
    "borderBottomWidth": "1px"
  }, themeStyle.background, themeStyle.border]}>
      <Wrap mode="light">
        {/* Desktop: single overflow-x-auto container spanning Hardware and
            columned Accessory rows (Accessory CAD Models). */}
        <_StyledDiv5>
          {hasHardware && <>
              <_StyledDiv6>
                <_StyledTableTitle title="Hardware" $_css4={[{
              "borderRightWidth": "1px",
              "@media (min-width: 1024px)": {
                "maxWidth": "400px"
              }
            }, themeStyle.border]} />
                {headings.map((heading, idx) => <_StyledTableCell5 key={idx}>
                    {heading}
                  </_StyledTableCell5>)}
              </_StyledDiv6>
              {hardwareRows.map((row, idx) => <TableRow key={`hw-${idx}`} row={row} headings={headings} />)}
            </>}
          {hasAccessoryColumns && <>
              <_StyledDiv7 $_css5={[{
            "display": "flex",
            "@media (min-width: 1024px)": {
              "borderTopWidth": "1px"
            }
          }, themeStyle.border]}>
                <_StyledTableTitle2 title="Hardware Accessories" $_css6={[{
              "@media (min-width: 1024px)": {
                "maxWidth": "400px"
              }
            }, themeStyle.border]} />
              </_StyledDiv7>
              {accessoryColumnRows.map((row, idx) => <TableRow key={`acc-col-${idx}`} row={row} headings={headings} />)}
            </>}
        </_StyledDiv5>

        {/* Full-width accessory rows (Y-cables) render outside the scroll
            container so they use the full page width. */}
        {hasAccessoryFullWidth && <_StyledDiv8>
            {!hasAccessoryColumns && <_StyledTableTitle3 title="Hardware Accessories" $_css7={[{
          "@media (min-width: 1024px)": {
            "maxWidth": "400px"
          }
        }, themeStyle.border]} />}
            {accessoryFullWidthRows.map((row, idx) => <TableRow key={`acc-full-${idx}`} row={row} headings={headings} />)}
          </_StyledDiv8>}

        {/* Mobile: one Select drives both sections. */}
        <_StyledDiv9>
          {hasHardware && <TableTitle title="Hardware" />}
          {hasItems(headings) && <_StyledDiv10 $_css8={[{
          "borderBottomWidth": "1px",
          "--tw-bg-opacity": "1",
          "backgroundColor": "rgb(227 227 240 / var(--tw-bg-opacity))",
          "padding": "2.4rem"
        }, themeStyle.border]}>
              <Select label="Select a Product" options={headings.map(heading => ({
            label: heading,
            value: heading
          }))} onChange={e => setActiveHeading(e.target.value)} defaultValue={headings[0]} />
            </_StyledDiv10>}
          {hasHardware && <_StyledDiv11 $_css9={[{
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
          },
          "@media (min-width: 1024px)": {
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
            }
          }
        }, themeStyle.border]}>
              {hardwareRows.map((row, idx) => <TableRow key={`hw-${idx}`} row={row} headings={headings} activeHeading={activeHeading} />)}
            </_StyledDiv11>}
          {(hasAccessoryColumns || hasAccessoryFullWidth) && <>
              <TableTitle title="Hardware Accessories" />
              <_StyledDiv12 $_css10={[{
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
            },
            "@media (min-width: 1024px)": {
              "> :not([hidden]) ~ :not([hidden])": {
                "--tw-divide-y-reverse": "0",
                "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
                "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
              }
            }
          }, themeStyle.border]}>
                {[...accessoryColumnRows, ...accessoryFullWidthRows].map((row, idx) => <TableRow key={`acc-${idx}`} row={row} headings={headings} activeHeading={activeHeading} />)}
              </_StyledDiv12>
            </>}
        </_StyledDiv9>
      </Wrap>
    </_StyledSection>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1uni0cz-0"
})`${p => p.$_css}`;
var _StyledTableCell = _styled2(TableCell).withConfig({
  componentId: "sc-sf-1uni0cz-1"
})({
  "@media (min-width: 1024px)": {
    "minWidth": "400px",
    "maxWidth": "400px"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1uni0cz-2"
})({
  "flex": "1 1 0%"
});
var _StyledTableCell2 = _styled4(TableCell).withConfig({
  componentId: "sc-sf-1uni0cz-3"
})({
  "flex": "1 1 0%"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1uni0cz-4"
})`${p => p.$_css2}`;
var _StyledTableCell3 = _styled6(TableCell).withConfig({
  componentId: "sc-sf-1uni0cz-5"
})({
  "@media (min-width: 1024px)": {
    "minWidth": "400px",
    "maxWidth": "400px"
  }
});
var _StyledDiv4 = _styled7("div").withConfig({
  componentId: "sc-sf-1uni0cz-6"
})({
  "flex": "1 1 0%"
});
var _StyledTableCell4 = _styled8(TableCell).withConfig({
  componentId: "sc-sf-1uni0cz-7"
})({
  "minWidth": "240px"
});
var _StyledSection = _styled9("section").withConfig({
  componentId: "sc-sf-1uni0cz-8"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled10("div").withConfig({
  componentId: "sc-sf-1uni0cz-9"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "none",
  "width": "100%",
  "flexDirection": "column",
  "overflowX": "auto",
  "@media (min-width: 1024px)": {
    "display": "flex"
  }
});
var _StyledDiv6 = _styled11("div").withConfig({
  componentId: "sc-sf-1uni0cz-10"
})({
  "display": "flex"
});
var _StyledTableTitle = _styled12(TableTitle).withConfig({
  componentId: "sc-sf-1uni0cz-11"
})`${p => p.$_css4}`;
var _StyledTableCell5 = _styled13(TableCell).withConfig({
  componentId: "sc-sf-1uni0cz-12"
})({
  "minWidth": "240px",
  "borderTopWidth": "0px"
});
var _StyledDiv7 = _styled14("div").withConfig({
  componentId: "sc-sf-1uni0cz-13"
})`${p => p.$_css5}`;
var _StyledTableTitle2 = _styled15(TableTitle).withConfig({
  componentId: "sc-sf-1uni0cz-14"
})`${p => p.$_css6}`;
var _StyledDiv8 = _styled16("div").withConfig({
  componentId: "sc-sf-1uni0cz-15"
})({
  "display": "none",
  "width": "100%",
  "flexDirection": "column",
  "@media (min-width: 1024px)": {
    "display": "flex"
  }
});
var _StyledTableTitle3 = _styled17(TableTitle).withConfig({
  componentId: "sc-sf-1uni0cz-16"
})`${p => p.$_css7}`;
var _StyledDiv9 = _styled18("div").withConfig({
  componentId: "sc-sf-1uni0cz-17"
})({
  "display": "block",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv10 = _styled19("div").withConfig({
  componentId: "sc-sf-1uni0cz-18"
})`${p => p.$_css8}`;
var _StyledDiv11 = _styled20("div").withConfig({
  componentId: "sc-sf-1uni0cz-19"
})`${p => p.$_css9}`;
var _StyledDiv12 = /*#__PURE__*/_styled21("div").withConfig({
  displayName: "hardware-and-accessories-table___StyledDiv12",
  componentId: "sc-hp3esk-0"
})(["", ""], p => p.$_css10);